import {
  Experiment,
  useChatSalesPortalDynamicConfigValue,
} from '@/experiences/sales-portal/blocs/hooks/use-chat-dynamic-config';
import { useMemo } from 'react';

export interface ExperimentResult {
  enabled: boolean;
  loading: boolean;
  metadata?: Experiment['metadata'];
}

interface UseSalesPortalExperimentParams {
  chatUUID?: string | null;
  experimentKey: string;
  supplierUUID: string;
}

/**
 * Hook to get experiment status from supplier and chat config.
 * Checks chat config, then falls back to supplier config.
 *
 * Uses a memoized Map for O(1) experiment lookups instead of linear search.
 *
 * @param params - The parameters for the hook
 * @param params.chatUUID - The chat UUID (optional)
 * @param params.experimentKey - The experiment key to check
 * @param params.supplierUUID - The supplier UUID
 * @returns The experiment result with enabled status, loading state, and optional metadata
 *
 * @example
 * ```typescript
 * const { enabled, loading, metadata } = useSalesPortalExperiment({
 *   experimentKey: 'sales-portal-accounts-v1',
 *   supplierUUID: 'supplier-789',
 * });
 *
 * if (loading) {
 *   return <Loading />;
 * }
 *
 * if (enabled) {
 *   // Feature is enabled, metadata contains experiment-specific data
 *   console.log(metadata?.variant);
 * }
 * ```
 */
export const useSalesPortalExperiment = ({
  experimentKey,
  supplierUUID,
  chatUUID,
}: UseSalesPortalExperimentParams): ExperimentResult => {
  const { loading, value: experiments } = useChatSalesPortalDynamicConfigValue(
    supplierUUID,
    'experiments',
    chatUUID,
  );

  // Create a memoized Map of experiments by key for O(1) lookup
  // The Map is only rebuilt when experiments change, not when experimentKey changes
  const experimentsMap = useMemo(() => {
    const exps = (experiments as Experiment[] | null) ?? [];
    return new Map(exps.map((exp) => [exp.key, exp]));
  }, [experiments]);

  const result = useMemo(() => {
    const exp = experimentsMap.get(experimentKey);
    if (exp) {
      return { enabled: true, metadata: exp.metadata };
    }
    return { enabled: false };
  }, [experimentsMap, experimentKey]);

  return { ...result, loading };
};
